import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBar from "@/sot/primitives/TopBar.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import { supabase } from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  buildVitalRepSourceSnapshot,
  upsertVitalRepDraftForAppointment,
} from "@/pages/doctors/appointments/lib/vitalRepNote.js";

const CLEAR_ICON = "/icons/doctor/cancel.svg";

function readAppointmentDraft(appointmentId) {
  const key = `afrimed:doctor_appt:${String(appointmentId)}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeAppointmentDraft(appointmentId, payload) {
  const key = `afrimed:doctor_appt:${String(appointmentId)}`;
  try {
    sessionStorage.setItem(key, JSON.stringify(payload));
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function readPrescriptionDraft(appointmentId) {
  const key = `afrimed:rx:draft:${String(appointmentId)}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePrescriptionDraft(appointmentId, payload) {
  const key = `afrimed:rx:draft:${String(appointmentId)}`;
  try {
    sessionStorage.setItem(key, JSON.stringify(payload));
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export default function DoctorDiagnosisNotes() {
  const nav = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams();
  const { t } = useLanguage();

  const appointmentDraft = React.useMemo(
    () => readAppointmentDraft(appointmentId) || location.state || null,
    [appointmentId, location.state]
  );
  const rxDraft = React.useMemo(() => readPrescriptionDraft(appointmentId) || null, [appointmentId]);

  const initialDiagnosis =
    appointmentDraft?.diagnosisTitle ||
    rxDraft?.diagnosisTitle ||
    rxDraft?.diagnosis ||
    "";
  const initialNotes =
    appointmentDraft?.diagnosisNotes ||
    appointmentDraft?.notes ||
    rxDraft?.diagnosisNotes ||
    rxDraft?.notes ||
    "";

  const [diagnosisTitle, setDiagnosisTitle] = React.useState(initialDiagnosis);
  const [notes, setNotes] = React.useState(initialNotes);
  const [saving, setSaving] = React.useState(false);
  const diagnosisInputRef = React.useRef(null);
  const notesTextareaRef = React.useRef(null);
  const pageTitle = t("doctors.diagnosisNotes.title", "Diagnosis and Notes");
  const pageSubtitle = t(
    "doctors.diagnosisNotes.subtitle",
    "Add the visit diagnosis and supporting notes.",
  );
  const doneLabel = t("common.actions.done", "Done");
  const diagnosisLabel = t("doctors.diagnosisNotes.fields.diagnosis", "Diagnosis");
  const diagnosisPlaceholder = t(
    "doctors.diagnosisNotes.fields.diagnosisPlaceholder",
    "Ex: Diabetes",
  );
  const notesLabel = t("doctors.diagnosisNotes.fields.notes", "Notes");

  const canSave =
    !saving &&
    (
      diagnosisTitle.trim() !== initialDiagnosis.trim() ||
      notes.trim() !== initialNotes.trim()
    );
  const diagnosisClearButtonClassName =
    "absolute right-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center";
  const notesClearButtonClassName =
    "absolute right-2 top-2 grid h-11 w-11 place-items-center";
  const clearButtonStyle = {
    borderRadius: 8,
    WebkitTapHighlightColor: "transparent",
  };

  function handleClearDiagnosis() {
    setDiagnosisTitle("");
    requestAnimationFrame(() => diagnosisInputRef.current?.focus());
  }

  function handleClearNotes() {
    setNotes("");
    requestAnimationFrame(() => notesTextareaRef.current?.focus());
  }

  async function handleDone() {
    if (!canSave) return;

    const diagnosisValue = diagnosisTitle.trim();
    const notesValue = notes.trim();

    setSaving(true);
    try {
      const nextAppointmentDraft = {
        ...(appointmentDraft || {}),
        diagnosisTitle: diagnosisValue,
        diagnosisNotes: notesValue,
        notes: notesValue,
      };

      writeAppointmentDraft(appointmentId, nextAppointmentDraft);

      const nextRxDraft = {
        ...(rxDraft || { appointmentId: String(appointmentId) }),
        diagnosisTitle: diagnosisValue,
        diagnosisNotes: notesValue,
        diagnosis: diagnosisValue,
        notes: notesValue,
        updatedAtIso: new Date().toISOString(),
      };

      writePrescriptionDraft(appointmentId, nextRxDraft);

      try {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData?.user?.id || null;
        if (userId) {
          await supabase
            .from("appointments")
            .update({
              diagnosis_title: diagnosisValue || null,
              diagnosis_notes: notesValue || null,
              notes: notesValue,
              updated_at: new Date().toISOString(),
            })
            .eq("id", appointmentId)
            .eq("doctor_id", userId);

          const sourceSnapshot = buildVitalRepSourceSnapshot({
            appointmentId,
            restoredSnapshot: nextAppointmentDraft,
            diagnosisTitle: diagnosisValue,
            diagnosisNotes: notesValue,
            notes: notesValue,
          });

          await upsertVitalRepDraftForAppointment({
            appointmentId,
            doctorUserId: userId,
            sourceSnapshot,
            endedAt:
              nextAppointmentDraft?.video_ended_at ||
              nextAppointmentDraft?.videoEndedAt ||
              new Date().toISOString(),
          }).catch(() => null);
        }
      } catch {
        // best effort only
      }

      window.dispatchEvent(new Event("afrimed:appointments:changed"));

      nav(`/doctors/appointments/${appointmentId}`, {
        replace: true,
        state: nextAppointmentDraft,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-[100dvh]" data-hide-tabbar="true">
      <SettingsScreenShell className="pb-24">
        <TopBar
          title={pageTitle}
          subtitle={pageSubtitle}
          onBack={() => nav(-1)}
          trailing={
            canSave ? (
              <SaveChip disabled={!canSave} onClick={handleDone}>
                {doneLabel}
              </SaveChip>
            ) : null
          }
        />

        <div className="mt-4 space-y-7">
          <div>
            <div className="relative rounded-[12px] bg-[rgb(var(--afrimed-auth-input-surface))] px-4 py-2.5">
              <label
                htmlFor="doctor-diagnosis-title"
                className="mb-[1px] block select-none text-label-medium tracking-[0.5px] text-[rgb(var(--afrimed-auth-input-label,91,107,134))]"
              >
                {diagnosisLabel}
              </label>
              <div className="flex min-h-[1.75rem] items-center">
                <input
                  ref={diagnosisInputRef}
                  id="doctor-diagnosis-title"
                  name="diagnosis"
                  type="text"
                  autoComplete="off"
                  inputMode="text"
                  autoCapitalize="words"
                  value={diagnosisTitle}
                  onChange={(e) => setDiagnosisTitle(e.target.value)}
                  placeholder={diagnosisPlaceholder}
                  className="block min-h-[1.75rem] w-full appearance-none border-none bg-transparent pr-12 text-body-large text-[rgb(var(--afrimed-auth-input-text,4,8,18))] outline-none placeholder:text-[rgb(var(--afrimed-auth-input-label,91,107,134))] focus:ring-0"
                />
              </div>

              {diagnosisTitle ? (
                <button
                  type="button"
                  onClick={handleClearDiagnosis}
                  className={diagnosisClearButtonClassName}
                  style={clearButtonStyle}
                  aria-label={t("common.actions.clear", "Clear")}
                >
                  <img
                    src={CLEAR_ICON}
                    alt=""
                    className="h-5 w-5"
                    draggable="false"
                  />
                </button>
              ) : null}
            </div>
          </div>

          <div className="relative rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
            <div className="flex items-start gap-3">
              <label htmlFor="doctor-diagnosis-notes" className="text-body-large text-on-surface-var">
                {notesLabel}
              </label>
            </div>
            {notes ? (
              <button
                type="button"
                onClick={handleClearNotes}
                className={notesClearButtonClassName}
                style={clearButtonStyle}
                aria-label={t("common.actions.clear", "Clear")}
              >
                <img
                  src={CLEAR_ICON}
                  alt=""
                  className="h-5 w-5"
                  draggable="false"
                />
              </button>
            ) : null}
            <textarea
              ref={notesTextareaRef}
              id="doctor-diagnosis-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 min-h-[112px] w-full resize-none bg-transparent pr-12 text-body-large text-on-surface outline-none placeholder:text-on-surface-var"
              placeholder=""
            />
          </div>
        </div>
      </SettingsScreenShell>
    </div>
  );
}
